import React, { ReactNode } from 'react'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { formatEmailLink, formatTelLink } from '@cbgms/base/utils/helpers'
import { useGetCurrentShopID, useGetCurrentShopName } from 'app/shops/hooks/useGetCurrentShopID'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { BottomBar } from './components/BottomBar'
import { MenuColumn } from './components/MenuColumn'
import { MenuItem } from './components/MenuItem'
import { TicketDialog } from 'app/contact/support/TicketDialog'
import { useCurrentEmployee } from 'app/employees/hooks/use-current-employee'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { Typography } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import StoreIcon from '@mui/icons-material/Store'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import Tooltip from '@mui/material/Tooltip'
interface InfoMenuItemProps {
  value?: ReactNode | string | null
  linkFormatter?: (value: string) => string
  icon: React.ReactNode
  tooltip?: string
}

const AppFooter: React.FC = () => {
  const { t } = useTranslation()

  const { selectedOrderAccount } = useOrderAccountsContext()
  const { data: currentEmployee } = useCurrentEmployee()
  const shopID = useGetCurrentShopID()
  const shopName = useGetCurrentShopName()

  const [showTicketDialog, setShowTicketDialog] = React.useState(false)

  const wholesalerSupportTicketSystem = selectedOrderAccount?.Wholesaler?.TicketSystem
  const wholesalerBranchName = selectedOrderAccount?.BranchDetails?.BranchName
  const wholesalerBranchEmail = selectedOrderAccount?.BranchDetails?.BranchEmail
  const wholesalerBranchPhoneNumber = selectedOrderAccount?.BranchDetails?.BranchPhoneNumber
  const wholesalerBranchFullAddress = selectedOrderAccount?.BranchDetails?.BranchFullAddress

  const wholesalerName = selectedOrderAccount?.Wholesaler?.Name
  const wholesalerSupportEmail = selectedOrderAccount?.Wholesaler?.SupportEmail
  const wholesalerSupportPhoneNumber = selectedOrderAccount?.Wholesaler?.SupportPhoneNumber
  const wholesalerSupportFullAddress = selectedOrderAccount?.Wholesaler?.SupportFullAddress
  const isBranch = wholesalerBranchName || wholesalerBranchEmail || wholesalerBranchPhoneNumber || wholesalerBranchFullAddress
  interface InfoItem {
    value?: ReactNode | string | null
    linkFormatter?: (value: string) => string
    icon: React.ReactNode
    tooltip?: string
  }

  const infoItems: InfoItem[] = [
    {
      value: isBranch ? wholesalerBranchName : wholesalerName,
      tooltip: t('Footer.name'),
      icon: (
        <BusinessIcon data-testid='footer-name-icon' fontSize='medium' color='inherit' sx={{ marginBottom: 0, verticalAlign: 'middle' }} />
      )
    },
    {
      value: isBranch ? wholesalerBranchEmail : wholesalerSupportEmail,
      linkFormatter: formatEmailLink,
      tooltip: t('Footer.emailAddress'),
      icon: (
        <EmailIcon data-testid='footer-email-icon' fontSize='medium' color='inherit' sx={{ marginBottom: 0, verticalAlign: 'middle' }} />
      )
    },
    {
      value: isBranch ? wholesalerBranchPhoneNumber : wholesalerSupportPhoneNumber,
      linkFormatter: formatTelLink,
      tooltip: t('Footer.phoneNumber'),
      icon: (
        <LocalPhoneIcon
          data-testid='footer-phone-icon'
          fontSize='medium'
          color='inherit'
          sx={{ marginBottom: 0, verticalAlign: 'middle' }}
        />
      )
    },
    {
      value: isBranch ? wholesalerBranchFullAddress : wholesalerSupportFullAddress,
      tooltip: t('Footer.fullAddress'),
      icon: (
        <BusinessIcon
          data-testid='footer-address-icon'
          fontSize='medium'
          color='inherit'
          sx={{ marginBottom: 0, verticalAlign: 'middle' }}
        />
      )
    }
  ]
  const md = 3
  const xs = 6

  const InfoMenuItem: React.FC<InfoMenuItemProps> = ({ value, linkFormatter, icon, tooltip }) =>
    value ? (
      <MenuItem>
        <Stack direction='row' spacing={1}>
          <Tooltip title={tooltip || ''}>
            <span>{icon}</span>
          </Tooltip>
          {linkFormatter && typeof value === 'string' ? (
            <Link href={linkFormatter(value)} rel='nofollow' underline='hover' style={{ color: 'inherit' }}>
              {value}
            </Link>
          ) : (
            <Typography>{value}</Typography>
          )}
        </Stack>
      </MenuItem>
    ) : null

  return (
    <Stack direction='column' mt={0.5}>
      <Grid
        rowGap={2}
        container
        direction='row'
        wrap='nowrap'
        sx={{ backgroundColor: theme => theme.palette.LKQGrey.background.modest, color: theme => theme.palette.common.white, px: 2, py: 4 }}
      >
        <Grid item md={md} xs={xs}>
          <MenuColumn header={t('Footer.legalNotice')}>
            <MenuItem>
              <Link component={RouterLink} to='/settings/privacy-and-conditions' underline='hover' style={{ color: 'inherit' }}>
                {t('Footer.privacy')}
              </Link>
            </MenuItem>
            <MenuItem>
              <Link component={RouterLink} to='/settings/privacy-and-conditions' underline='hover' style={{ color: 'inherit' }}>
                {t('Footer.termsAndConditions')}
              </Link>
            </MenuItem>
            <MenuItem>
              <Link component={RouterLink} to='/settings/privacy-and-conditions' underline='hover' style={{ color: 'inherit' }}>
                {t('Footer.cookie')}
              </Link>
            </MenuItem>
          </MenuColumn>
        </Grid>
        <Grid item md={md} xs={xs}>
          <MenuColumn header={t('Footer.customerSupport')}>
            <MenuItem>
              <Link component={RouterLink} to='/contact' underline='hover' style={{ color: 'inherit' }}>
                {t('Footer.helpCenter')}
              </Link>
            </MenuItem>
            {wholesalerSupportTicketSystem && (
              <MenuItem>
                <Link onClick={() => setShowTicketDialog(true)} underline='hover' style={{ color: 'inherit', cursor: 'pointer' }}>
                  {t('Footer.needHelp')}
                </Link>
              </MenuItem>
            )}
          </MenuColumn>
        </Grid>
        <Grid item md={md} xs={xs}>
          <MenuColumn header={t('Footer.yourShop')}>
            <InfoMenuItem
              value={<Typography sx={{ paddingTop: 0.2 }}>{shopName}</Typography>}
              tooltip={t('Footer.name')}
              icon={
                <PersonIcon
                  data-testid='footer-person-icon'
                  fontSize='medium'
                  color='inherit'
                  sx={{ marginLeft: '-3px', verticalAlign: 'middle' }}
                />
              }
            />
            <InfoMenuItem
              value={<Typography sx={{ paddingTop: 0.2 }}>{shopID}</Typography>}
              tooltip={t('Footer.shopID')}
              icon={
                <StoreIcon
                  data-testid='footer-store-icon'
                  fontSize='medium'
                  color='inherit'
                  sx={{ marginLeft: '-3px', verticalAlign: 'middle' }}
                />
              }
            />
          </MenuColumn>
        </Grid>
        <Grid item md={md} xs={xs} sx={{ maxWidth: 'max-content !important' }}>
          <MenuColumn header={t('Footer.yourSupplier')}>
            {(() => {
              return (
                <>
                  {infoItems.map(({ icon, value, linkFormatter, tooltip }, idx) => (
                    <InfoMenuItem key={idx} icon={icon} value={value} linkFormatter={linkFormatter} tooltip={tooltip} />
                  ))}
                </>
              )
            })()}
          </MenuColumn>
        </Grid>
      </Grid>
      <BottomBar />
      <TicketDialog
        shopID={shopID}
        shopName={shopName}
        employee={currentEmployee}
        orderAccount={selectedOrderAccount}
        open={showTicketDialog}
        handleClose={() => setShowTicketDialog(false)}
      />
    </Stack>
  )
}

const AppFooterContainer = () => {
  const isPortal = useIsPortal()
  if (!isPortal) {
    return null
  }
  return <AppFooter />
}
export default AppFooterContainer
